import { Injectable } from '@angular/core';
import {
  Allocation,
  AllocationConseil,
  AllocationPartenaireStatut,
  DisponibiliteEpargne,
  FicheConseil,
  HorizonPlacementEnum,
  NiveauDeRisque,
  ToleranceRisque,
} from '@lib/models/generated/graphql';
import { gql } from 'apollo-angular';
import { QueryManagerService } from 'finzzle-angular-utils';
import _ from 'lodash';
import { OuiBannerDeclination } from 'omnium-ui/banner';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

const niveauDeRisqueOrderMapping: { [key in NiveauDeRisque]: number } = {
  [NiveauDeRisque.Securise]: 1,
  [NiveauDeRisque.Prudent]: 2,
  [NiveauDeRisque.Equilibre]: 3,
  [NiveauDeRisque.Dynamique]: 4,
  [NiveauDeRisque.Offensif]: 5,
};

const ProfilQPICodeMap: { [key in string]: number } = {
  OPPOSE: 1,
  SECURITAIRE: 2,
  PRUD_DEFENSIF: 3,
  EQ_DYNAMIQUE: 4,
  AGRESSIF: 5,
};

export enum HorizonRetraite {
  AN_0_3 = '0-3Ans',
  AN_3_5 = '3-5Ans',
}

export enum PatrimoineWarning {
  VI10 = '10%',
  VI30 = '30%',
  VI50 = '50%',
}

export type AllocationDisplay = {
  disabledNiveauDeRisque: Set<NiveauDeRisque>;
  showBanner: boolean;
  showDispoEpargne: boolean;
  showTolerancePerte: boolean;
  toleranceRisqueProjet: ToleranceRisque | undefined;
  horizonRetraite: HorizonRetraite | undefined;
  horizonPlacement: HorizonPlacementEnum | undefined;
  profilRisque: NiveauDeRisque | undefined;
  patrimoineWarning: PatrimoineWarning[];
  bannerDeclination: OuiBannerDeclination;
  bannerTitle: string;
};

export const allocationBannerSomeDisabled: string = 'Certains choix de profils de risque ont été désactivés';
export const allocationBannerAllDisabled: string =
  'Tous les choix de profils de risque disponibles pour ce partenaire ont été désactivés';

export const ALLOCATIONS_BY_PRODUIT = gql`
  query allocationsByProduit($produitId: Long!) {
    allocationsByProduit(produitId: $produitId) {
      id
      storageFileId
      modeDeGestionCode
      niveauDeRisque
      libelle
      statut
      storageFile {
        id
        fileName
        fileType
        fileExtension
        isBlob
      }
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class AllocationService {
  private allocations: Allocation[] = [];
  public allocationsByProduitSubject = new BehaviorSubject<Allocation[]>(this.allocations);

  public allocationBusinessRule: AllocationDisplay = {
    disabledNiveauDeRisque: new Set<NiveauDeRisque>(),
    showBanner: false,
    showDispoEpargne: false,
    showTolerancePerte: false,
    toleranceRisqueProjet: undefined,
    horizonRetraite: undefined,
    horizonPlacement: undefined,
    profilRisque: undefined,
    patrimoineWarning: [],
    bannerDeclination: 'info',
    bannerTitle: '',
  };

  get allocationRule(): AllocationDisplay {
    return this.allocationBusinessRule;
  }
  constructor(
    private queryManager: QueryManagerService
  ) {}

  async loadAllocationsByProduit(produitId: number | null): Promise<void> {
    if (produitId === null || isNaN(produitId)) {
      this.allocations = [];
      this.allocationsByProduitSubject.next(this.allocations);
      return;
    }
    const { data } = await lastValueFrom(
      this.queryManager.query<{ allocationsByProduit: Allocation[] }>({
        query: ALLOCATIONS_BY_PRODUIT,
        variables: { produitId },
      })
    );

    this.allocations = data?.allocationsByProduit ?? [];
    this.allocationsByProduitSubject.next(this.allocations);
  }

  public get produitAllocations(): Allocation[] {
    return this.allocations;
  }

  private get produitAllocationsByModeGestionCode(): Record<string, Allocation[]> {
    return _.groupBy(this.allocations, 'modeDeGestionCode');
  }

  public getProduitAllocationByModeGestion(gestionCode: string | null): Allocation[] {
    if (!gestionCode) return [];
    return this.produitAllocationsByModeGestionCode[gestionCode] ?? [];
  }

  public computeAllocationRule(fiche: FicheConseil): AllocationDisplay {
    this.allocationBusinessRule = this.checkAllocationAndNiveauDeRisqueRules(fiche);
    return this.allocationBusinessRule;
  }
  private getProfilQPIValue(profilQpiCode: string | null | undefined): number | null {
    return profilQpiCode ? (ProfilQPICodeMap[profilQpiCode] ?? null) : null;
  }
  /**
   * Returns the lower profil QPI code between QPiCode1 and QPiCode2
   * @param QPiCode1 first profil QPI code
   * @param QPiCode2 second profil QPI code
   * @returns the lower profil QPI code or null if both are null
   */
  private getLowerProfilQPI(QPiCode1: string | null | undefined, QPiCode2: string | null | undefined): string | null {
    const profilQpi1 = this.getProfilQPIValue(QPiCode1);
    const profilQpi2 = this.getProfilQPIValue(QPiCode2);
    if (!profilQpi2) {
      return QPiCode1 ?? null;
    }
    if (profilQpi1) {
      const lower = profilQpi1 < profilQpi2 ? QPiCode1 : QPiCode2;
      return lower ?? null;
    }
    return null;
  }
  private checkAllocationAndNiveauDeRisqueRules(fiche: FicheConseil): AllocationDisplay {

    // mapping on ProfilQpi.code
    const qpiSECURISE = 'OPPOSE';
    const qpiPRUDENT = 'SECURITAIRE';
    const qpiEQUILIBRE = 'PRUD_DEFENSIF';

    const computedDisplay: AllocationDisplay = {
      disabledNiveauDeRisque: new Set<NiveauDeRisque>(),
      showBanner: false,
      showDispoEpargne: false,
      showTolerancePerte: false,
      toleranceRisqueProjet: undefined,
      horizonRetraite: undefined,
      horizonPlacement: undefined,
      profilRisque: undefined,
      patrimoineWarning: [],
      bannerDeclination: 'info',
      bannerTitle: allocationBannerSomeDisabled,
    };
    // Message #1 - Disponibilité de l'épargne
    const disponibiliteEpargne = fiche.disponibiliteEpargne;
    if (disponibiliteEpargne == DisponibiliteEpargne.ToujoursDispo) {
      computedDisplay.showDispoEpargne = true;
    }
    // Message #2 - Capacité à supporter les pertes
    //FIXME waiting for spec for tolerance perte with new profil data: US23072 https://omnium-finance.visualstudio.com/ConseilPlacement/_workitems/edit/23072


    // inactivate profile for message 1 and 2
    if (computedDisplay.showTolerancePerte || computedDisplay.showDispoEpargne) {
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Prudent);
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Equilibre);
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
    }

    // Message #3 - Tolérance au risque du projet
    const toleranceRisqueProjet = fiche?.toleranceRisque ?? undefined;
    computedDisplay.toleranceRisqueProjet = toleranceRisqueProjet;

    switch (toleranceRisqueProjet) {
      case ToleranceRisque.Securise:
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Prudent);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Equilibre);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
        break;
      case ToleranceRisque.Prudent:
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Equilibre);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
        break;
      case ToleranceRisque.Equilibre:
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
        break;
      case ToleranceRisque.Dynamique:
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
        break;
      default:
        break;
    }

    // Message #4 - Horizon de placement
    const horizonPlacementValue = fiche?.horizonPlacement?.value;
    const horizonRetraiteAnnées = fiche?.horizonPlacement?.horizonRetraite;
    switch (horizonPlacementValue) {
      case HorizonPlacementEnum.Retraite:
        if (horizonRetraiteAnnées !== null && horizonRetraiteAnnées !== undefined) {
          if (horizonRetraiteAnnées >= 0 && horizonRetraiteAnnées < 3) {
            computedDisplay.horizonPlacement = HorizonPlacementEnum.Retraite;
            computedDisplay.horizonRetraite = HorizonRetraite.AN_0_3;

            computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Prudent);
            computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
            computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Equilibre);
            computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
          }
          if (horizonRetraiteAnnées >= 3 && horizonRetraiteAnnées < 5) {
            computedDisplay.horizonPlacement = HorizonPlacementEnum.Retraite;
            computedDisplay.horizonRetraite = HorizonRetraite.AN_3_5;
            computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
            computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Equilibre);
            computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
          }
        }
        break;
      case HorizonPlacementEnum.De0A3Ans:
        computedDisplay.horizonPlacement = HorizonPlacementEnum.De0A3Ans;
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Prudent);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Equilibre);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
        break;
      case HorizonPlacementEnum.De3A5Ans:
        computedDisplay.horizonPlacement = HorizonPlacementEnum.De3A5Ans;
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Equilibre);
        computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
        break;
    }

    // Message #5 et 6 - Versement initial
    // Message #7 - Profil de risque investisseur

    // here we compare the profilQpiCode of the investisseur and co-investisseur and take the lower value
    const qpiCode = this.getLowerProfilQPI(
      fiche.investisseur?.profil?.profilQpiCode,
      fiche.coInvestisseur?.profil?.profilQpiCode
    );

    // TODO: Restore patrimoine threshold checks when the new patrimoine model is available.


    const montantVI = fiche.versements.versementInitial?.montantBrut;
    computedDisplay.patrimoineWarning = [];
    //SECURISE
    if (qpiCode && qpiCode === qpiSECURISE) {
      computedDisplay.profilRisque = NiveauDeRisque.Securise;
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Prudent);
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Dynamique);
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Equilibre);
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
    }
    //PRUDENT
    if (qpiCode && qpiCode === qpiPRUDENT) {

      computedDisplay.profilRisque = NiveauDeRisque.Prudent;
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
    }
    //EQUILIBRE
    if (qpiCode && qpiCode === qpiEQUILIBRE) {
      computedDisplay.profilRisque = NiveauDeRisque.Equilibre;
      computedDisplay.disabledNiveauDeRisque.add(NiveauDeRisque.Offensif);
    }
    //DYNAMIQUE

    computedDisplay.showBanner = computedDisplay.disabledNiveauDeRisque.size > 0;
    this.allocationBusinessRule = computedDisplay;
    return computedDisplay;
  }

  generateNiveauDeRisqueOptions(): OuiSelectOption<NiveauDeRisque>[] {
    const options: OuiSelectOption<NiveauDeRisque>[] = [
      {
        label: 'Sécurisé (SRI 1)',
        value: NiveauDeRisque.Securise,
        disabled: this.allocationBusinessRule.disabledNiveauDeRisque.has(NiveauDeRisque.Securise),
      },
      {
        label: 'Prudent (SRI 2)',
        value: NiveauDeRisque.Prudent,
        disabled: this.allocationBusinessRule.disabledNiveauDeRisque.has(NiveauDeRisque.Prudent),
      },
      {
        label: 'Equilibré (SRI 3)',
        value: NiveauDeRisque.Equilibre,
        disabled: this.allocationBusinessRule.disabledNiveauDeRisque.has(NiveauDeRisque.Equilibre),
      },
      {
        label: 'Dynamique (SRI 4 et 5)',
        value: NiveauDeRisque.Dynamique,
        disabled: this.allocationBusinessRule.disabledNiveauDeRisque.has(NiveauDeRisque.Dynamique),
      },
      {
        label: 'Offensif (SRI 6 et 7)',
        value: NiveauDeRisque.Offensif,
        disabled: this.allocationBusinessRule.disabledNiveauDeRisque.has(NiveauDeRisque.Offensif),
      },
    ];
    return options.sort((a, b) => {
      if (a.value && b.value) {
        return niveauDeRisqueOrderMapping[a.value] - niveauDeRisqueOrderMapping[b.value];
      }
      return 0;
    });
  }

  buildAllocationPartenairesOptions(fiche: FicheConseil) {
    const allocation = this.getProduitAllocationByModeGestion(fiche.modeDeGestion?.code ?? null);
    const allocationPartenaireOptions = this.getProduitAllocationByModeGestion(fiche.modeDeGestion?.code ?? null)
      .filter(allocation => allocation?.statut === AllocationPartenaireStatut.Actif)
      .map(allocation => ({
        label: allocation.libelle,
        value: {
          id: allocation.id,
          libelle: allocation.libelle,
          modeDeGestionCode: allocation.modeDeGestionCode,
          niveauDeRisque: allocation.niveauDeRisque,
        } as AllocationConseil,
        disabled: this.allocationBusinessRule.disabledNiveauDeRisque.has(allocation.niveauDeRisque),
      }));

    // check if all disabled
    const isAllOptionsDisabled = allocationPartenaireOptions.every(option => option.disabled);
    if (isAllOptionsDisabled) {
      this.allocationBusinessRule.bannerTitle = allocationBannerAllDisabled;
      this.allocationBusinessRule.bannerDeclination = 'error';
    } else {
      this.allocationBusinessRule.bannerTitle = allocationBannerSomeDisabled;
      this.allocationBusinessRule.bannerDeclination = 'info';
    }
    // sort by Niveau de Risque
    return allocationPartenaireOptions.sort((a, b) => {
      if (a.value.niveauDeRisque && b.value.niveauDeRisque) {
        return niveauDeRisqueOrderMapping[a.value.niveauDeRisque] - niveauDeRisqueOrderMapping[b.value.niveauDeRisque];
      }
      return 0;
    });
  }
}
